import * as React from "react"

import { cn } from "@/lib/utils"

const formatNumber = (val) => {
  if (val === null || val === undefined || val === '') return '';
  let str = val.toString();
  if (str === '-') return '-';
  
  let parts = str.split('.');
  if (parts.length > 2) return str;
  
  let integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  let decimalPart = parts[1] !== undefined ? `,${parts[1]}` : '';
  
  return integerPart + decimalPart;
}

const parseFormattedNumber = (val) => {
  if (!val && val !== 0) return '';
  let str = val.toString();
  
  let parsed = str.replace(/\./g, '').replace(/,/g, '.');
  parsed = parsed.replace(/[^\d.-]/g, '');
  
  let p = parsed.split('.');
  if (p.length > 2) {
    parsed = p[0] + '.' + p.slice(1).join('');
  }
  
  return parsed;
}

const Input = React.forwardRef(({ className, type, value, onChange, onBlur, ...props }, ref) => {
  const isNumberType = type === 'number';
  
  const [displayValue, setDisplayValue] = React.useState(() => {
    return isNumberType ? formatNumber(value) : value;
  });

  React.useEffect(() => {
    if (isNumberType) {
      const parsedCurrent = parseFormattedNumber(displayValue);
      if (value !== undefined && value !== null && value.toString() !== parsedCurrent) {
         setDisplayValue(formatNumber(value));
      } else if (value === '' || value === null || value === undefined) {
         setDisplayValue('');
      }
    }
  }, [value, isNumberType, displayValue]);

  const handleChange = (e) => {
    if (isNumberType) {
      let rawInput = e.target.value;
      
      if (rawInput.endsWith('.') && !displayValue.endsWith('.')) {
        if (!rawInput.slice(0, -1).includes(',')) {
          rawInput = rawInput.slice(0, -1) + ',';
        }
      }

      rawInput = rawInput.replace(/[^\d.,-]/g, '');
      
      const parsed = parseFormattedNumber(rawInput);
      const formatted = formatNumber(parsed);
      
      setDisplayValue(formatted);
      
      if (onChange) {
        const fakeEvent = {
          ...e,
          target: { ...e.target, value: parsed, name: e.target.name }
        };
        onChange(fakeEvent);
      }
    } else {
      if (type !== 'password' && type !== 'email' && type !== 'url') {
        const raw = e.target.value;
        // Hanya auto-kapitalisasi jika user sedang mengetik di akhir kalimat (normal typing).
        // Ini memungkinkan user untuk kembali ke tengah kata dan mengubah menjadi huruf kecil secara manual.
        if (e.target.selectionStart === raw.length) {
          e.target.value = raw.replace(/(^\w|\s\w)/g, c => c.toUpperCase());
        } else {
          e.target.value = raw;
        }
      }
      if (onChange) onChange(e);
    }
  };

  const handleBlur = (e) => {
    if (isNumberType) {
      const parsed = parseFormattedNumber(displayValue);
      setDisplayValue(formatNumber(parsed));
    }
    if (onBlur) onBlur(e);
  };

  return (
    <input
      type={isNumberType ? 'text' : type}
      inputMode={isNumberType ? 'decimal' : undefined}
      value={isNumberType ? displayValue : value}
      onChange={handleChange}
      onBlur={handleBlur}
      className={cn(
        "flex h-9 w-full rounded-xl border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-all duration-200 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      onWheel={(e) => {
        if (isNumberType) {
          e.target.blur();
        }
        if (props.onWheel) {
          props.onWheel(e);
        }
      }}
      {...props}
    />
  );
})
Input.displayName = "Input"

export { Input }
